import saveAs from "file-saver";
import JSZip from "jszip";
import { render, TemplateResult } from "lit";
import { Entidad } from "../../../../data/Entidad";
import { IPagoInfoFacturarConceptoComplemento } from "../../../../data/entidad/Factura";
import { _DICC_ALUMNO, _SvAlumnoObtenerCurp } from "../../../../data/modulo/Alumno";
import { _DICC_ESCOLARIDAD } from "../../../../data/modulo/Escolaridad";
import { _SvFacturaObtenerArchivoCFDI } from "../../../../data/modulo/FacturaCFDI";
import { DataUtilAlertBot } from "../../../../data/util/AlertBot";
import _L, { _HttpMsgV2, TLabelTag } from "../../../../util/Labels";
import { FullViewFile } from "../../../controlD3/FullViewFile";
import { List } from "../../../controlD3/List";
import { _ModalSimple } from "../../../controlD3/ModalThings/ModalSimple";
import { NotificacionV2 } from "../../../controlD3/NotificacionV2";
import { HTMLFileIcoElement } from "../../../controlWC/FileIcoComponent";
import { HTMLProgressElement } from "../../../controlWC/ProgressComponent";
import { UIUtilElement } from "../../../util/Element";
import { UIUtilGeneral } from "../../../util/Util";
import ic_sync from '/image/iconos/ic_sync.svg?raw';

/**
 * Namespace que agrupa utilidades relacionadas con la facturación del estado de cuenta de un alumno.
 */
export namespace AlumnoEdoCtaFacturacionUtil {
    export const _MODULO_ALUMNO_EDOCTA = Entidad.CModulo.PanelFinanzasEdoCuenta;

    /**
     * Crea un control de lista con estilos y configuraciones opcionales.
     * @param options Opciones para agregar márgenes superior e inferior.
     * @returns Una instancia del control de lista.
     */
    export function _CreateListControl<T>(options?: { addMarginBottom?: boolean, addMarginTop?: boolean }) {
        const list = new List<T>()
        list._ListContainerSelection.style("border-top", "1px solid var(--color_borderbox2)")
        if (options?.addMarginBottom) list._ListContainerSelection.style("margin-bottom", "50px")
        if (options?.addMarginTop) list._ListContainerSelection.style("margin-top", "var(--padding2)")
        return list
    }

    export type SinRegistrosComponent = HTMLElement & {
        _SetText(text: string): SinRegistrosComponent
        _Visible(visible: boolean): SinRegistrosComponent
    }
    /**
     * Crea un componente para mostrar un mensaje cuando no hay registros disponibles.
     * @param container Contenedor HTML donde se renderizará el componente.
     * @returns Un componente con métodos para actualizar su visibilidad y texto.
     */
    export function _CreateSinRegistrosComponent(container: TSelectionHTML) {
        const element: SinRegistrosComponent = UIUtilElement._CreateElementFromHTML(
            `<div><div style="margin: var(--padding2) auto; width: max-content;">
                TEXTO
            </div></div>`) as any
        element._SetText = (text) => ((element.querySelector(":scope>div").textContent = text), element)
        element._Visible = (visible) => ((visible ? container.node().append(element) : element.remove()), element)
        return element
    }

    /**
     * Genera una fila de información con un formato específico.
     * @param tag Etiqueta o título de la fila.
     * @param txt Texto o contenido de la fila.
     * @param br Indica si se debe agregar un salto de línea antes de la fila.
     * @param infoExtra Indica si el contenido es información adicional.
     * @returns Una cadena HTML con el formato de la fila.
     */
    export const _ViewRowInfo = (tag: TLabelTag, txt: string, br = true, infoExtra?: boolean) => {
        return _ViewRowInfoBase(_L(tag), txt, br, infoExtra)
    }

    /**
     * Genera una fila de información con un formato específico.
     * @param tag Etiqueta o título de la fila.
     * @param txt Texto o contenido de la fila.
     * @param br Indica si se debe agregar un salto de línea antes de la fila.
     * @param infoExtra Indica si el contenido es información adicional.
     * @returns Una cadena HTML con el formato de la fila.
     */
    export const _ViewRowInfoBase = (tag: string, txt: string, br = true, esInfoExtra?: boolean) => {
        const fontSizePxDif = esInfoExtra ? "3px" : null
        const style = fontSizePxDif ? `style="font-size: calc(var(--fontsize) - ${fontSizePxDif});"` : ""
        return (br ? "<br>" : "")
            + `<b ${style}>${tag || ""}${(!txt || !tag) ? "" : ":"}</b> <span ${style}>${txt}</span>`;
    }

    /**
     * Genera un elemento de lista con un checkbox y contenido personalizado.
     * @param container Contenedor HTML donde se renderizará el elemento.
     * @param checked Indica si el checkbox debe estar marcado.
     * @param contentHTML Contenido HTML que se mostrará junto al checkbox.
     */
    export const _ViewItemListCheck = (container: TSelectionHTML, checked: boolean, contentHTML: string | TemplateResult) => container
        .classed(`${UIUtilGeneral.FBoxOrientation.Horizontal} ${UIUtilGeneral.FBoxAlign.StartCenter}`, true)
        .style("gap", "var(--padding2)")
        .style("cursor", "pointer")
        .html(
            `<div class="checkbox_cont">
                <wc-checkbox
                    style-form="circle-check"
                    ${checked ? "checked" : ""}
                    style="width:20px;height:20px;"
                    disabled>
                </wc-checkbox>
            </div>
            <div class="listitem_cont_info" style="width:100%">
            </div>`
        )
        .call(section => {
            if (typeof contentHTML === "string") {
                section.select(".listitem_cont_info").html(contentHTML);
            } else {
                render(contentHTML, section.select(".listitem_cont_info").node() as HTMLElement);
            }
        });

    /**
     * Agrega un botón de sincronización a un contenedor.
     * @param container Contenedor HTML donde se agregará el botón.
     * @param call Función que se ejecutará al hacer clic en el botón.
     */
    export function _ViewAddSyncButton(container: TSelectionHTML, call: Function) {
        let btn = container.append<HTMLDivElement>("wc-button")
            .style("width", "20px")
            .style("height", "20px")
            .style("position", "absolute")
            .style("top", "var(--padding2)")
            .style("right", "var(--padding2)")
            .on("click", () => call())

        const svgIco = UIUtilElement._CreateElementFromHTML(ic_sync)
        svgIco.style.width = "100%"
        svgIco.style.height = "100%"
        btn.property("_HtmlElement", svgIco)
    }

    // /**
    //  * Abre un modal para descargar archivos CFDI asociados a un folio.
    //  * @param UUID Identificador único del CFDI.
    //  * @param idEscuela ID de la escuela asociada.
    //  * @param idFolio ID del folio asociado.
    //  * @deprecated
    //  */
    // export function _OpenModal_ViewDescargarCFDIFiles_ByFolio(UUID: string, idEscuela: number, idFolio: number) {
    //     ModalThings._GetModalSimple({
    //         Title: _L("panelfinanzasedocuenta_factura.cfdi") + " ..." + UUID,
    //         Width: 250,
    //         DrawContent: (container, mt) => {
    //             container
    //                 .style("display", "flex")
    //                 .style("flex-direction", "column")
    //                 .style("gap", "var(--padding2)")

    //             _ViewDescargarCFDIFiles(container, idEscuela, idFolio, mt.Progress.node())

    //             new Button(container, _L("general.descargartodo"))._d3Selection
    //                 .style("margin-top", "var(--padding1)")
    //                 .on("click", () => _DescargarCFDIsZip(null, idEscuela, [idFolio], mt.Progress.node()))
    //         }
    //     })
    // }

    export function _VerArchivoCFDI(idEscuela: number, idFolio: number, ext: "xml" | "pdf") {
        new FullViewFile()
            ._SetContent([{
                Id: 1,
                Filename: `CFDI_${idFolio}.${ext}`,
                FileExt: ext,
                Content: () => _SvFacturaObtenerArchivoCFDI(idEscuela, idFolio, ext).then(res => res.Datos)
            }])
    }

    /**
     * Genera la vista para descargar archivos CFDI (.xml, .pdf).
     * @param container Contenedor HTML donde se renderizará la vista.
     * @param idEscuela ID de la escuela asociada.
     * @param idFolio ID del folio asociado.
     * @param progressBar Barra de progreso para mostrar el estado de la descarga.
     */
    export function _ViewDescargarCFDIFiles(container: TSelectionHTML, idEscuela: number, idFolio: number, progressBar: HTMLProgressElement) {
        container.append("label").text(_L("panelfinanzasedocuenta_factura.descargar_doc") + ":")
        container.append("div").call((cont) => {
            const filesExt = ["xml", "pdf"] as const
            cont.classed(UIUtilGeneral.FBoxOrientation.Horizontal, true)
                .style("gap", "var(--padding2)")
            filesExt.forEach(ext => cont.append<HTMLFileIcoElement>("wc-fileico")
                .attr("ext", ext.toUpperCase())
                .style("width", "45px")
                .style("height", "55px")
                .style("cursor", "pointer")
                .on("click", () => _DescargarCFDI(idEscuela, idFolio, ext, progressBar)))
        })
    }

    /**
     * Descarga un archivo CFDI (.xml o .pdf) asociado a un folio.
     * @param idEscuela ID de la escuela asociada.
     * @param idFolio ID del folio asociado.
     * @param ext Extensión del archivo a descargar ("xml" o "pdf").
     * @param progressBar Barra de progreso para mostrar el estado de la descarga.
     */
    export function _DescargarCFDI(idEscuela: number, idFolio: number, ext: "xml" | "pdf", progressBar: HTMLProgressElement) {
        progressBar._Visible = true
        _SvFacturaObtenerArchivoCFDI(idEscuela, idFolio, ext)
            .then((res) => {
                if (res.Resultado <= 0) {
                    NotificacionV2._Mostrar(_HttpMsgV2(res, "consulta"), "ADVERTENCIA")
                    return
                }
                saveAs(res.Datos)
            })
            .finally(() => {
                progressBar._Visible = false
            })
    }

    /**
     * Descarga múltiples archivos CFDI (.xml, .pdf) como un archivo ZIP.
     * @param folderName Nombre de la carpeta dentro del ZIP.
     * @param idEscuela ID de la escuela asociada.
     * @param idsFolios IDs de los folios asociados.
     * @param progressBar Barra de progreso para mostrar el estado de la descarga.
     */
    export async function _DescargarCFDIsZip(folderName: string | null, idEscuela: number, idsFolios: number[], progressBar: HTMLProgressElement) {
        progressBar._Visible = true
        const filesExt = ["xml", "pdf"] as const
        const files: File[] = []
        for (const idFolio of idsFolios) {
            for (const ext of filesExt) {
                const res = await _SvFacturaObtenerArchivoCFDI(idEscuela, idFolio, ext)
                if (res.Resultado <= 0 || !res.Datos) {
                    NotificacionV2._Mostrar(_HttpMsgV2(res, "consulta"), "ADVERTENCIA")
                    progressBar._Visible = false
                    return
                }
                files.push(res.Datos)
            }
        }
        progressBar._Visible = false
        const folderNameFinal = idsFolios.length == 1 ? files[0].name.split(".")[0] : folderName ? (folderName + "_CFDIs") : "CFDIs"
        const zip = new JSZip();
        const resumenFolder = zip.folder(folderNameFinal);
        for (const file of files) {
            resumenFolder.file(file.name, file)
        }
        zip.generateAsync({ type: "blob" })
            .then(function (content) {
                saveAs(content, folderNameFinal);
            })
            .catch((e) => {
                DataUtilAlertBot._SendError(e, `Descarga CFDIs zip`);
                NotificacionV2._Mostrar(_L("general.notif_fail"), "ADVERTENCIA")
            })
    }

    export interface ValidaInfoRequeridaComplementoIEDUOptions {
        forceRequestCurp?: boolean
        shouldNotify?: boolean
        onChangePath?: () => void
    }
    export interface InfoRequeridaComplementoIEDU {
        valid: boolean;
        curp: string | null;
        autRVOE: string | null;
        nivelSAT: string | null;
    }
    export async function _ValidaInfoRequeridaComplementoIEDU(idAlumno: number, options?: ValidaInfoRequeridaComplementoIEDUOptions): Promise<InfoRequeridaComplementoIEDU> {
        const alumno = _DICC_ALUMNO.get(idAlumno);
        const result: InfoRequeridaComplementoIEDU = { valid: false, curp: null, autRVOE: null, nivelSAT: null };
        if (!alumno) {
            console.warn("alumno no encontrado:", idAlumno);
            return result;
        }

        // Valida CURP

        result.curp = await _SvAlumnoObtenerCurp(idAlumno, alumno.IdKinder, !!options?.forceRequestCurp)
            .then<string>(res => {
                if (res.Resultado == 1) {
                    return res.CURP // CURP obtenida correctamente
                }
                if (options?.shouldNotify) {
                    if (res.Resultado === -1) {
                        NotificacionV2._Mostrar(_L("panelfinanzasedocuenta_factura.compl_fail_getcurp_404_escuela"), "ADVERTENCIA");
                    } else if (res.Resultado === -2) {
                        NotificacionV2._Mostrar(_L("panelfinanzasedocuenta_factura.compl_fail_getcurp"), "ADVERTENCIA");
                    } else if (res.Resultado === -3) {
                        _ModalSimple({
                            Title: _L("general.notif_infofalta"),
                            Message: _L("panelfinanzasedocuenta_factura.compl_fail_getcurp_404_alumno"),
                            DrawContent: (content, mt) => {
                                content.append("br")
                                content.append("br")
                                const l = content.append("label")
                                UIUtilGeneral._ElementAdd_LinkToGoToPanel(l,
                                    "alumnos/alumnos/panel",
                                    alumno.IdChild,
                                    _L("panelfinanzasedocuenta_factura.compl_fail_getcurp_alumno_ver_infoextra"),
                                    () => {
                                        mt.Modal._Ocultar();
                                        options?.onChangePath?.();
                                    },
                                    "palumnoinfoextra")
                            }
                        })
                    }
                }
                return null
            });

        // if (!result.curp) {
        //     return result;
        // }

        // Valida información de nivel educativo referente al complemento IEDU

        const escolaridadInfo = _ObtenerInfoFiscalNivelEscolar(alumno.IdEscolaridad);

        result.autRVOE = escolaridadInfo?.AutRVOE || null;
        result.nivelSAT = escolaridadInfo?.NivelSAT || null;

        if (options?.shouldNotify && result.curp && (!result.autRVOE || !result.nivelSAT)) {
            _ModalSimple({
                Title: _L("general.notif_infofalta"),
                Message: _L("panelfinanzasedocuenta.msg_nivel_info_requerida", escolaridadInfo?.Nombre),
                DrawContent: (content, mt) => {
                    content.append("br")
                    content.append("br")
                    const l = content.append("label")
                    UIUtilGeneral._ElementAdd_LinkToGoToPanel(l,
                        "escuelas/escuelas/panel",
                        alumno.IdKinder,
                        _L("panelfinanzasedocuenta.msg_nivel_info_requerida_tag_ver"),
                        () => {
                            mt.Modal._Ocultar();
                            options?.onChangePath?.();
                        },
                        null,
                        {
                            "maintab": "niveles",
                            "nivel": alumno.IdEscolaridad,
                        })
                }
            })
        }
        result.valid = !!(result.curp && result.autRVOE && result.nivelSAT);
        return result;
    }

    export function _ObtenerInfoFiscalNivelEscolar(idEscolaridad: number): Pick<Entidad.IEscolaridad, "AutRVOE" | "NivelSAT" | "Nombre"> | null {
        const escolaridad = _DICC_ESCOLARIDAD.get(idEscolaridad);
        if (!escolaridad) {
            console.warn("escolaridad no encontrada:", idEscolaridad);
            return null;
        }
        return {
            AutRVOE: escolaridad.AutRVOE,
            NivelSAT: escolaridad.NivelSAT,
            Nombre: escolaridad.Nombre
        };
    }

    export async function _CreateComplementoIEDUByAlumnoID(idAlumno: number): Promise<IPagoInfoFacturarConceptoComplemento> {
        const alumno = _DICC_ALUMNO.get(idAlumno);
        if (!alumno) {
            throw new Error(`Alumno no encontrado: ${idAlumno}`);
        }
        return _CreateComplementoIEDUByAlumno(alumno);
    }

    export async function _CreateComplementoIEDUByAlumno(alumno: Entidad.IAlumno): Promise<IPagoInfoFacturarConceptoComplemento> {
        const curp = await _SvAlumnoObtenerCurp(alumno.IdChild, alumno.IdKinder, false)
            .then(req => req.Resultado == 1 ? req.CURP : null)
            .catch(() => null);

        const escolaridadInfo = _ObtenerInfoFiscalNivelEscolar(alumno.IdEscolaridad);

        return {
            NombreAlumno: alumno.NombreCompleto || "",
            CURP: curp || "",
            AutRVOE: escolaridadInfo?.AutRVOE || "",
            NivelEducativo: escolaridadInfo?.NivelSAT || "",
            RFCPago: "",
        }
    }
}